import React, { useMemo } from "react";
import {
    BASEMENT_WALL_EXTRA_INSULATION_LAYER,
    INSULATION_LAYER_1,
    INSULATION_LAYER_2,
    INTERIOR,
    SHEATHING,
    EXTERIOR,
    FRAME_CAVITY_FRACTIONS,
} from "utils/enclosure/embodiedCarbon";
import GradeDisplay from "./GradeDisplay";
import PercentageDisplay from "./PercentageDisplay";
import EmbodiedCarbonFramingGroupGrade from "./framingGroupEmbodiedCarbon";
import EmbodiedCarbonStandardWindowCodeGrade from "./windowStandardEmbodiedCarbon";
import EmbodiedCarbonCalculationsSummary from "./CalculationsSummary";

const LAYER_DATA_MAP = {
    insulationLayer1: INSULATION_LAYER_1,
    insulationFramingLayer: INSULATION_LAYER_1,
    insulationLayer2: INSULATION_LAYER_2,
    extraInsulationLayer: BASEMENT_WALL_EXTRA_INSULATION_LAYER,
    interior: INTERIOR,
    interiorFinish: INTERIOR,
    sheathing: SHEATHING,
    exterior: EXTERIOR,
};

export { default as GradeDisplay } from "./GradeDisplay";
export { default as PercentageDisplay } from "./PercentageDisplay";
export { default as StandardEmbodiedLayerBlock } from "./wallLayerEmbodiedCarbon";

/**
 * Inline grade display for a single layer field.
 * Place next to a Select field in the same InputRow.
 */
export const LayerGrade = React.memo(
    ({
        layerType,
        code,
        componentType = "wall",
        structureCode = "0",
        framingCode = "0", //Only used for basement wall
        spacingCode = "0", //Only used for insulation layer 1
        withBorder = false,
        borderHeight = "100%",
        customGap = "0.75rem",
        customPadding = "0 7px",
        barMetric = "percentage",
    }) => {
        const data = useMemo(() => {
            const map = LAYER_DATA_MAP[layerType];

            if (!map) return null;

            const codeStr = (code || "0").toString();

            const mapRow = map[codeStr];
            if (!mapRow) return null;

            let multiplier = 1;
            let embodiedCarbonLayerData = { ...mapRow };
            if (layerType === "insulationLayer1" || layerType === "insulationFramingLayer") {
                // if we're dealing with insulation layer 1, we need to multiply the emissions and storage by the cavity percentage

                if (componentType === "basementWall") {
                    const basementWallFramingType = ["0", "1", "2", "3", "4", "5", "6"].includes(framingCode)
                        ? "wood"
                        : "steel";
                    multiplier =
                        FRAME_CAVITY_FRACTIONS?.[componentType]?.[basementWallFramingType]?.[spacingCode]?.cavityArea /
                            100 || 1;
                } else {
                    multiplier =
                        FRAME_CAVITY_FRACTIONS?.[componentType]?.[structureCode]?.[spacingCode]?.cavityArea / 100 || 1;
                }

                if (embodiedCarbonLayerData) {
                    embodiedCarbonLayerData.emissions *= multiplier;
                    embodiedCarbonLayerData.storage *= multiplier;
                }
            }

            return embodiedCarbonLayerData;
        }, [layerType, code, componentType, structureCode, framingCode, spacingCode]);

        if (!data) return <div />;

        if (barMetric === "grade") {
            return (
                <GradeDisplay
                    emissions={data.emissions}
                    storage={data.storage}
                    emissionGrade={data.emissionGrade}
                    storageGrade={data.storageGrade}
                    withBorder={withBorder}
                    borderHeight={borderHeight}
                    customGap={customGap}
                    customPadding={customPadding}
                />
            );
        }

        return (
            <PercentageDisplay
                emissions={data.emissions}
                storage={data.storage}
                withBorder={withBorder}
                borderHeight={borderHeight}
                customGap={customGap}
                customPadding={customPadding}
            />
        );
    }
);

/** @deprecated Use EmbodiedCarbonFramingGroupGrade; kept for existing standard-code imports. */
const EmbodiedCarbonGroupGrade = EmbodiedCarbonFramingGroupGrade;

export default EmbodiedCarbonFramingGroupGrade;
export {
    EmbodiedCarbonFramingGroupGrade,
    EmbodiedCarbonGroupGrade,
    EmbodiedCarbonStandardWindowCodeGrade,
    EmbodiedCarbonCalculationsSummary
};
